import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../../services/authService';
import { columnValueLabel, normalizeColumnValues, preventDisallowedTypedKey, restrictTypedValue } from './criteriaUtils';

const suggestionCache = new Map();

function suggestionKey(condition) {
  if (!condition?.has_value_list) return '';
  const parts = [condition.catalog, condition.schema, condition.table, condition.column].map((v) => String(v || '').trim());
  return parts.every(Boolean) ? parts.join(' ') : '';
}

/**
 * Chip-style multi-value input for "is any of" / "is not any of".
 * For value-list columns, suggestions come from `/column-values` (shown by their
 * `value_label` when the data dictionary has one, while the stored operand stays the
 * raw value); otherwise free text (Enter or comma adds a chip; pasting comma/newline
 * separated text adds several).
 */
const MultiValueInput = ({ condition, values, onChange, numeric = false, textMode = null, hasError = false, single = false }) => {
  const [draft, setDraft] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const key = suggestionKey(condition);

  useEffect(() => {
    if (!key) {
      setSuggestions([]);
      return undefined;
    }
    if (suggestionCache.has(key)) {
      setSuggestions(suggestionCache.get(key));
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get('/api/v2/segments/column-values', {
        params: { catalog: condition.catalog, schema: condition.schema, table: condition.table, column_name: condition.column },
      })
      .then((res) => {
        const list = normalizeColumnValues(res.data);
        suggestionCache.set(key, list);
        if (!cancelled) setSuggestions(list);
      })
      .catch(() => {
        if (!cancelled) setSuggestions([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [key, condition.catalog, condition.schema, condition.table, condition.column]);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = draft.trim().toLowerCase();
    const chosen = new Set(values);
    return suggestions
      .filter(
        (s) =>
          !chosen.has(s.value) &&
          (!q || s.value.toLowerCase().includes(q) || s.label.toLowerCase().includes(q))
      )
      .slice(0, 40);
  }, [suggestions, draft, values]);

  const commit = (parts) => {
    const clean = parts.map((s) => String(s).trim()).filter(Boolean);
    if (!clean.length) return;
    const next = single ? [] : [...values];
    for (const p of clean) if (!next.includes(p)) next.push(p);
    onChange(single ? next.slice(-1) : next);
    setDraft('');
    setActive(0);
    if (single) setOpen(false);
  };
  const cleanTyped = (raw) => (textMode ? restrictTypedValue(raw, textMode) : String(raw ?? ''));
  /** Add typed text; commas / newlines split into several values (free-text columns only). */
  const add = (raw) => {
    const text = String(raw || '');
    const parts = condition.has_value_list || single ? [text] : text.split(/[\n,]/);
    commit(parts.map(cleanTyped));
  };
  /** Add one exact suggestion (never split — values may legitimately contain commas). */
  const addOne = (value) => commit([value]);

  const remove = (v) => onChange(values.filter((x) => x !== v));

  const onKeyDown = (e) => {
    const hasDraft = Boolean(draft.trim());
    if (e.key === 'Enter') {
      e.preventDefault();
      const picked = open && filtered.length ? filtered[active] : undefined;
      if (picked) addOne(picked.value);
      else if (hasDraft) add(draft);
    } else if (e.key === ',' && !condition.has_value_list) {
      if (hasDraft) {
        e.preventDefault();
        add(draft);
      } else {
        preventDisallowedTypedKey(e, textMode);
      }
    } else if (e.key === 'Backspace' && !draft && values.length) {
      remove(values[values.length - 1]);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setActive((a) => Math.min(a + 1, Math.max(filtered.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Escape') {
      setOpen(false);
    } else {
      preventDisallowedTypedKey(e, textMode);
    }
  };

  const showList = open && (filtered.length > 0 || loading);
  const placeholder = values.length
    ? ''
    : condition.has_value_list
      ? 'Choose values…'
      : textMode === 'digits' || numeric
        ? 'Type a number, press Enter'
        : 'Type a value, press Enter';

  return (
    <div ref={wrapRef} className="relative min-w-[220px] flex-1">
      <div
        className={`flex min-h-10 w-full flex-wrap items-center gap-1 rounded-lg border bg-surface px-2 py-1 text-sm focus-within:ring-1 focus-within:ring-[#18181B]/10 ${
          hasError ? 'border-red-300' : 'border-input'
        }`}
        onClick={() => inputRef.current?.focus()}
        role="presentation"
      >
        {values.map((v) => {
          const label = columnValueLabel(v, suggestions);
          return (
            <span key={v} className="inline-flex items-center gap-1 rounded-md bg-[#E8EEF5] px-2 py-0.5 text-xs text-[#18181B]" title={label === v ? undefined : v}>
              {label}
              <button type="button" onClick={() => remove(v)} className="text-zinc-400 hover:text-zinc-700" aria-label={`Remove ${label}`}>
                <X className="h-3 w-3" />
              </button>
            </span>
          );
        })}
        <input
          ref={inputRef}
          type="text"
          inputMode={textMode === 'digits' || numeric ? 'numeric' : 'text'}
          autoComplete="off"
          value={draft}
          onChange={(e) => {
            setDraft(cleanTyped(e.target.value));
            setOpen(true);
            setActive(0);
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Don't lose half-typed free text when focus moves on (value lists must pick a suggestion).
            if (draft.trim() && !condition.has_value_list) add(draft);
          }}
          onKeyDown={onKeyDown}
          onPaste={(e) => {
            const text = e.clipboardData?.getData('text') || '';
            if (!single && !condition.has_value_list && /[\n,]/.test(text)) {
              e.preventDefault();
              add(text);
            }
          }}
          placeholder={placeholder}
          className="h-7 min-w-[8rem] flex-1 bg-transparent text-sm placeholder:text-zinc-400 focus:outline-none"
          aria-label="Values"
        />
      </div>
      {showList ? (
        <ul className="absolute z-40 mt-1 max-h-56 w-full overflow-auto rounded-lg border border-hairline bg-surface p-1 shadow-card" role="listbox">
          {loading ? <li className="px-2 py-1.5 text-xs text-zinc-400">Loading values…</li> : null}
          {filtered.map((s, i) => (
            <li key={s.value} role="presentation">
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => addOne(s.value)}
                title={s.label === s.value ? undefined : s.value}
                className={`flex w-full items-center rounded-md px-2 py-1.5 text-left text-sm ${
                  i === active ? 'bg-[#E8EEF5] text-[#18181B]' : 'text-zinc-700 hover:bg-[#E8EEF5]'
                }`}
              >
                {s.label}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default MultiValueInput;
